/**
 * Bind a delegated event listener: `handler` fires whenever the event
 * reaches `root` having passed through an element matching `selector`.
 *
 * @param {Element | Document} root - Ancestor to attach the listener to.
 * @param {string} type - Event type, e.g. "click".
 * @param {string} selector - CSS selector matched against the event target's ancestors.
 * @param {(event: Event, matched: Element) => void} handler - Called with the event and the matched element.
 * @param {AddEventListenerOptions} [options] - Listener options, e.g. `{ capture: true }` for non-bubbling events like `focus`.
 * @returns {() => void} A function that removes the listener.
 */
export function on(root, type, selector, handler, options) {
    /**
     * Forward the event to `handler` if its target matches `selector`.
     *
     * @param {Event} event - The dispatched event.
     * @returns {void}
     */
    const listener = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const matched = target.closest(selector);
        if (matched && root.contains(matched)) {
            handler(event, matched);
        }
    };

    root.addEventListener(type, listener, options);

    return () => root.removeEventListener(type, listener, options);
}
